import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Chip, Button, Divider, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Rating, Stack, List, ListItem, ListItemText, CircularProgress, Tooltip, Alert,
  TextField, LinearProgress,
} from '@mui/material';
import {
  ArrowBack, Edit, Verified, OpenInNew, Description, ShoppingCart,
  Email, Phone, Language, Business, LocationOn, Inventory2, AttachFile,
  CloudUpload, Delete,
} from '@mui/icons-material';
import api from '../../services/api';

const STATUS_COLOR = {
  prospect: 'default', contacted: 'info', samples_requested: 'primary',
  samples_received: 'secondary', evaluating: 'warning', approved: 'success',
  rejected: 'error', on_hold: 'default',
};
const STATUS_LABEL = {
  prospect: 'Prospect', contacted: 'Contacted', samples_requested: 'Samples Requested',
  samples_received: 'Samples Received', evaluating: 'Evaluating', approved: 'Approved',
  rejected: 'Rejected', on_hold: 'On Hold',
};
const SOURCE_LABEL = {
  alibaba: 'Alibaba', made_in_china: 'Made-in-China', referral: 'Referral',
  tradeshow: 'Trade Show', google: 'Google', linkedin: 'LinkedIn', other: 'Other',
};

const qty = (v) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const formatBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n/1024).toFixed(1)} KB`;
  return `${(n/(1024*1024)).toFixed(2)} MB`;
};

const InfoLine = ({ icon, label, value, link }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
    <Box sx={{ color: 'action.active', mt: 0.3 }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{label}</Typography>
      {link && value ? (
        <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none', fontSize: 14 }}>{value}</a>
      ) : (
        <Typography variant="body2">{value || '—'}</Typography>
      )}
    </Box>
  </Box>
);

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [files, setFiles]       = useState([]);
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [uploading, setUploading] = useState(false);
  const [fileDescription, setFileDescription] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, f, o] = await Promise.all([
        api.get(`/sourcing-suppliers/${id}`),
        api.get(`/sourcing-suppliers/${id}/products`),
        api.get(`/sourcing-suppliers/${id}/files`),
        api.get(`/sourcing-orders?supplier_type=sourcing&search=`),
      ]);
      setSupplier(s.data.data);
      setProducts(p.data.data || []);
      setFiles(f.data.data || []);
      // Filter orders matching this supplier
      const linked = (o.data.data || []).filter(x => x.sourcing_supplier_id === parseInt(id));
      setOrders(linked);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load supplier');
    }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openFile = async (fileId) => {
    try {
      const res = await api.get(`/sourcing-suppliers/files/${fileId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { alert('Unable to open file'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (fileDescription) fd.append('description', fileDescription);
    try {
      await api.post(`/sourcing-suppliers/${id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const f = await api.get(`/sourcing-suppliers/${id}/files`);
      setFiles(f.data.data || []);
      setFileDescription('');
    } catch (err) { alert(err.response?.data?.message || 'Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Remove "${f.original_name}"?`)) return;
    await api.delete(`/sourcing-suppliers/files/${f.id}`);
    setFiles(files.filter(x => x.id !== f.id));
  };

  const handlePromote = async () => {
    if (!window.confirm(`Promote "${supplier.company_name}" to active beneficiary?`)) return;
    try {
      await api.post(`/sourcing-suppliers/${id}/promote`, { country: supplier.country, currency: 'USD' });
      load();
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!supplier) return null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/sourcing/discover')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, fontWeight: 700 }}>{supplier.company_name}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5, gap: 1 }}>
            <Chip size="small" label={STATUS_LABEL[supplier.status] || supplier.status}
              color={STATUS_COLOR[supplier.status]} />
            <Chip size="small" label={SOURCE_LABEL[supplier.source] || supplier.source} variant="outlined" />
            {supplier.rating && <Rating value={supplier.rating} readOnly size="small" />}
            {supplier.approved_beneficiary_id && (
              <Chip size="small" icon={<Verified />} label="Promoted to Beneficiary" color="success" variant="outlined" />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
          {supplier.status === 'approved' && !supplier.approved_beneficiary_id && (
            <Button variant="outlined" color="success" size="small" startIcon={<Verified />} onClick={handlePromote}>
              Promote
            </Button>
          )}
          <Button variant="contained" size="small" startIcon={<Edit />}
            onClick={() => navigate(`/sourcing/discover?edit=${id}`)}>Edit</Button>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {/* Left column — Contact + Location */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business fontSize="small" /> Contact Information
            </Typography>
            <InfoLine icon={<Business fontSize="small" />} label="Contact Person" value={supplier.contact_person} />
            <InfoLine icon={<Email fontSize="small" />} label="Email" value={supplier.email}
              link={supplier.email ? `mailto:${supplier.email}` : null} />
            <InfoLine icon={<Phone fontSize="small" />} label="Phone" value={supplier.phone} />
            <InfoLine icon={<span style={{fontWeight:700, fontSize:11}}>WC</span>} label="WeChat" value={supplier.wechat_id} />
            <Divider sx={{ my: 1.5 }} />
            <InfoLine icon={<LocationOn fontSize="small" />} label="Location"
              value={[supplier.city, supplier.country].filter(Boolean).join(', ')} />
            <InfoLine icon={<Language fontSize="small" />} label="Website" value={supplier.website}
              link={supplier.website} />
            <InfoLine icon={<OpenInNew fontSize="small" />} label="Alibaba / Listing"
              value={supplier.alibaba_url ? 'View Listing' : null} link={supplier.alibaba_url} />
          </Paper>
        </Grid>

        {/* Right column — Categories + Evaluation */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Product Categories</Typography>
            <Box sx={{ mb: 2 }}>
              {supplier.product_categories ? (
                supplier.product_categories.split(',').map((c, i) => (
                  <Chip key={i} label={c.trim()} size="small" sx={{ mr: 0.5, mb: 0.5 }} variant="outlined" />
                ))
              ) : <Typography color="text.secondary">—</Typography>}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}>Strengths</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{supplier.strengths || '—'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}>Weaknesses</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{supplier.weaknesses || '—'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}>Notes</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{supplier.notes || '—'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Products */}
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 fontSize="small" /> Products ({products.length})
            </Typography>
            {products.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>No products listed.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">MOQ</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Lead Time</TableCell>
                      <TableCell align="center">Sample</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map(p => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{p.product_name}</Typography>
                          {p.specifications && (
                            <Typography variant="caption" color="text.secondary">{p.specifications}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{p.moq ? `${qty(p.moq)} ${p.unit_of_measure || ''}` : '—'}</TableCell>
                        <TableCell align="right">{p.unit_price ? `${p.currency || ''} ${p.unit_price}` : '—'}</TableCell>
                        <TableCell align="right">{p.lead_time_days ? `${p.lead_time_days} d` : '—'}</TableCell>
                        <TableCell align="center">
                          {p.sample_available ? <Chip size="small" label="Yes" color="success" /> : <Chip size="small" label="No" variant="outlined" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Files */}
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachFile fontSize="small" /> Files ({files.length})
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField size="small" label="Description (optional)"
                  placeholder="Quote, sample photo, business license, certifications…"
                  value={fileDescription} onChange={e => setFileDescription(e.target.value)} sx={{ flex: 1 }} />
                <Button component="label" variant="contained" startIcon={<CloudUpload />} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload File'}
                  <input type="file" hidden onChange={handleUpload} />
                </Button>
              </Stack>
              {uploading && <LinearProgress sx={{ mt: 1 }} />}
            </Paper>

            {files.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                No files uploaded yet. Use the box above to attach quotes, sample photos, certifications, etc.
              </Typography>
            ) : (
              <List dense>
                {files.map(f => (
                  <ListItem key={f.id} divider sx={{ pl: 0 }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Open"><IconButton size="small" onClick={() => openFile(f.id)}>
                          <OpenInNew fontSize="small" /></IconButton></Tooltip>
                        <IconButton size="small" color="error" onClick={() => deleteFile(f)}><Delete fontSize="small" /></IconButton>
                      </Stack>
                    }>
                    <Description sx={{ mr: 1.5, color: 'action.active' }} />
                    <ListItemText
                      primary={<Box component="span" onClick={() => openFile(f.id)}
                        sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        {f.original_name}
                      </Box>}
                      secondary={`${f.description ? f.description + ' · ' : ''}${formatBytes(f.size_bytes)} · ${f.uploaded_by_name || 'Unknown'} · ${f.created_at?.substring(0, 10)}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          {/* Related orders */}
          <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart fontSize="small" /> Related Orders ({orders.length})
            </Typography>
            {orders.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>No orders linked to this supplier yet.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                      <TableCell>Ref</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Required By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map(o => (
                      <TableRow key={o.id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/sourcing/orders/${o.id}`)}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{o.order_ref}</TableCell>
                        <TableCell>{o.product_name}</TableCell>
                        <TableCell align="right">{o.quantity || '—'} {o.unit_of_measure || ''}</TableCell>
                        <TableCell><Chip size="small" label={o.status} variant="outlined" /></TableCell>
                        <TableCell>{o.required_by ? o.required_by.substring(0, 10) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
